import { Fragment, useState, useMemo, useEffect } from 'react'
import {
  InputLabel,
  DeprecatedSingleSelect,
  WithTheme,
} from '@opallabs/lapidary'
import * as styles from './styles'
import { TransformContentInput } from './TransformContentInput'
import { variantOptions, translationOptions } from './variationOptions'

export type PromptProps = {
  type: string
  prompt: string
}

export type TransformContentProps = WithTheme & {
  initialPrompt?: string
  initialPromptType?: string
  inModal?: boolean
  onSetPrompt: (promptObject: PromptProps) => void
}

export const TransformContent: React.FC<TransformContentProps> = ({
  initialPrompt = '',
  initialPromptType = '',
  inModal = false,
  onSetPrompt,
  theme = 'dark',
}) => {
  const [variationType, setVariationType] = useState(initialPromptType)
  const [prompt, setPrompt] = useState(initialPrompt)
  const [replaceFrom, setReplaceFrom] = useState<string>('')
  const [replaceTo, setReplaceTo] = useState<string>('')
  const variationTypeLabel = inModal
    ? 'Transformation Type'
    : 'Transform Content 🤖'

  useEffect(() => {
    onSetPrompt({ type: variationType, prompt })
  }, [variationType, prompt, onSetPrompt])

  const initialPromptTypeOption = useMemo(() => {
    return initialPromptType
      ? variantOptions.find((option) => option.value === initialPromptType)
      : undefined
  }, [initialPromptType])

  const initialPromptOption = useMemo(() => {
    return initialPrompt.length && variationType === 'translate'
      ? translationOptions.find((option) => option.value === initialPrompt)
      : undefined
  }, [initialPrompt, variationType])

  const [initialPromptFind, initialPromptReplace] = useMemo(() => {
    if (initialPrompt.length && variationType === 'find_and_replace') {
      return initialPrompt.split(' with ')
    }
    return [undefined, undefined]
  }, [initialPrompt, variationType])

  return (
    <Fragment>
      <InputLabel required={false} text={variationTypeLabel} theme={theme} />
      <DeprecatedSingleSelect
        initialSelection={initialPromptTypeOption}
        theme={theme}
        onChange={(option) => {
          if (option == null) return
          setVariationType(option.value)
          setPrompt('')
        }}
        options={variantOptions}
        placeholder="Options"
      />
      {variationType == 'translate' && (
        <Fragment>
          <InputLabel
            required={false}
            text="Language"
            theme={theme}
            styleOverrides={styles.labelOverrides}
          />
          <DeprecatedSingleSelect
            initialSelection={initialPromptOption}
            isSearchable={true}
            theme={theme}
            onChange={(option) => {
              if (option == null) return
              setPrompt(option.value)
            }}
            options={translationOptions}
            placeholder="Translate to..."
          />
        </Fragment>
      )}
      {variationType == 'different_tone' && (
        <TransformContentInput
          initialPrompt={initialPrompt}
          label="Tone Prompt"
          onChange={(value: string) => {
            if (value == '') return
            setPrompt(value)
          }}
          placeholder="e.g. make it sound more casual"
          theme={theme}
        />
      )}
      {variationType == 'different_approach' && (
        <TransformContentInput
          initialPrompt={initialPrompt}
          label="Different Approach"
          onChange={(value: string) => {
            if (value == '') return
            setPrompt(value)
          }}
          placeholder="e.g. emphasize it's eco-friendly"
          theme={theme}
        />
      )}
      {variationType == 'find_and_replace' && (
        <Fragment>
          <TransformContentInput
            initialPrompt={initialPromptFind}
            label="Find Text or Concept"
            onChange={(value: string) => {
              if (value == '') return
              setReplaceFrom(value)
              setPrompt(`${value} with ${replaceTo}`)
            }}
            placeholder="e.g. a product name or 'references to Summer'"
            theme={theme}
          />
          <TransformContentInput
            initialPrompt={initialPromptReplace}
            label="Replace With"
            onChange={(value: string) => {
              if (value == '') return
              setReplaceTo(value)
              setPrompt(`${replaceFrom} with ${value}`)
            }}
            placeholder="e.g. a different product or 'references to Winter'"
            theme={theme}
          />
        </Fragment>
      )}
    </Fragment>
  )
}
